/**
 * TanStack Query hooks for Policy Management
 * Provides policy CRUD operations, deployment, and lifecycle management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { apiClient, get, post, put, del } from '@/lib/api-client'
import { isApiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import type {
  PolicyDraft,
  PolicyDraftListItem,
  PolicyListResponse,
  PolicyQueryParams,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  BulkDeleteRequest,
  BulkDeleteResponse,
  DeployResponse,
  UndeployRequest,
  UndeployResponse,
  ValidateResponse,
} from '@/types/policy'

/**
 * Query keys for policy management
 * Provides consistent cache key structure
 */
export const policyKeys = {
  all: ['policies'] as const,
  lists: () => [...policyKeys.all, 'list'] as const,
  list: (params: PolicyQueryParams) => [...policyKeys.lists(), params] as const,
  details: () => [...policyKeys.all, 'detail'] as const,
  detail: (id: string) => [...policyKeys.details(), id] as const,
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch list of policy drafts with pagination and filters
 *
 * Query hook for fetching paginated list of policy drafts.
 * Automatically includes tenant context from auth store.
 *
 * @param params - Query parameters (page, pageSize, status filter)
 * @returns Query result with policy drafts array and pagination metadata
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = usePolicies({ page: 1, pageSize: 10 })
 * if (data) {
 *   console.log(data.data) // Array of policy drafts
 *   console.log(data.pagination) // Pagination metadata
 * }
 * ```
 */
export function usePolicies(params: PolicyQueryParams = {}) {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: policyKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams()

      if (params.page !== undefined) {
        queryParams.set('page', String(params.page))
      }
      if (params.pageSize !== undefined) {
        queryParams.set('pageSize', String(params.pageSize))
      }
      if (params.status) {
        queryParams.set('status', params.status)
      }

      const queryString = queryParams.toString()
      const url = queryString
        ? `/policy-drafts?${queryString}`
        : '/policy-drafts'

      return get<PolicyListResponse>(url)
    },
    // Keep previous data while fetching new page
    placeholderData: (previousData) => previousData,
    // 30 second stale time - policy list doesn't change very frequently
    staleTime: 30 * 1000,
    // Only fetch if user is authenticated
    enabled: !!tenantId,
  })
}

/**
 * Fetch a single policy draft by ID
 *
 * Query hook for fetching a policy draft with full details including
 * XACML content and human-readable description.
 *
 * @param id - Policy draft UUID (undefined will disable the query)
 * @returns Query result with policy draft details
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePolicy(policyId)
 * if (data) {
 *   console.log(data.xacmlPolicy) // Full XACML policy content
 *   console.log(data.humanReadable) // Human readable description
 * }
 * ```
 */
export function usePolicy(id: string | undefined) {
  // Normalize id - handle undefined, null, and string "undefined"
  const normalizedId = id && id !== 'undefined' && id !== 'null' ? id : undefined

  return useQuery({
    queryKey: normalizedId
      ? policyKeys.detail(normalizedId)
      : ['policies', 'detail', 'disabled'],
    queryFn: () => {
      if (!normalizedId) {
        // This should never execute due to enabled: false, but add safety check
        return Promise.reject(new Error('Policy ID is required'))
      }
      return get<PolicyDraft>(`/policy-drafts/${normalizedId}`)
    },
    // Only fetch when we have a valid policy ID
    enabled: !!normalizedId,
    // Don't retry if policy not found (404)
    retry: false,
    // Refetch on window focus to get latest status
    refetchOnWindowFocus: true,
    // Short stale time - policies may be updated by other users
    staleTime: 10 * 1000, // 10 seconds
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new policy draft
 *
 * Mutation hook for creating a new policy draft.
 * Automatically invalidates the policy list cache and navigates to the new policy.
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useCreatePolicy()
 *
 * const handleCreate = () => {
 *   mutate({
 *     name: 'My Policy',
 *     description: 'Policy description',
 *     xacmlPolicy: { PolicyId: 'policy-1', ... }
 *   })
 * }
 * ```
 */
export function useCreatePolicy() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (request: CreatePolicyRequest) => {
      return post<PolicyDraft, CreatePolicyRequest>('/policy-drafts', request)
    },
    onSuccess: (data) => {
      // Invalidate policy list to show new policy
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      toast.success('Policy draft created successfully')

      // Navigate to new policy detail
      navigate(`/pac/policies/${data.id}`)
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to create policy: ${message}`)
    },
  })
}

/**
 * Update a policy draft
 *
 * Mutation hook for updating a policy draft's name, description, or XACML content.
 * Invalidates both the policy list and detail caches.
 *
 * @param policyId - The policy draft ID to update
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useUpdatePolicy(policyId)
 *
 * const handleUpdate = () => {
 *   mutate({ name: 'Updated Policy Name' })
 * }
 * ```
 */
export function useUpdatePolicy(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: UpdatePolicyRequest) => {
      return put<PolicyDraft, UpdatePolicyRequest>(
        `/policy-drafts/${policyId}`,
        request
      )
    },
    onSuccess: () => {
      // Invalidate policy detail to refresh
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })

      // Invalidate policy list to show updated info
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      toast.success('Policy updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to update policy: ${message}`)
    },
  })
}

/**
 * Delete a single policy draft
 *
 * Mutation hook for deleting a policy draft. Invalidates the policy list
 * cache and navigates back to the policies list.
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useDeletePolicy()
 *
 * const handleDelete = (id: string) => {
 *   if (confirm('Delete this policy?')) {
 *     mutate(id)
 *   }
 * }
 * ```
 */
export function useDeletePolicy() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async (policyId: string) => {
      return del(`/policy-drafts/${policyId}`)
    },
    onSuccess: (_, policyId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: policyKeys.detail(policyId),
      })

      // Invalidate policy list
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      // Navigate to policies list
      navigate('/pac/policies')

      toast.success('Policy deleted successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to delete policy: ${message}`)
    },
  })
}

/**
 * Bulk delete multiple policy drafts
 *
 * Mutation hook for deleting multiple policies in a single transaction.
 * Note: Deployed policies cannot be bulk deleted - they must be deactivated first.
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const { mutate, isPending } = useBulkDeletePolicies()
 *
 * const handleBulkDelete = (ids: string[]) => {
 *   if (confirm(`Delete ${ids.length} policies?`)) {
 *     mutate({ ids })
 *   }
 * }
 * ```
 */
export function useBulkDeletePolicies() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: BulkDeleteRequest) => {
      return post<BulkDeleteResponse, BulkDeleteRequest>(
        '/policy-drafts/bulk-delete',
        request
      )
    },
    onSuccess: (data) => {
      // Remove deleted policies from cache
      data.deletedIds.forEach((id) => {
        queryClient.removeQueries({
          queryKey: policyKeys.detail(id),
        })
      })

      // Invalidate policy list
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      toast.success(`Successfully deleted ${data.count} policies`)
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to delete policies: ${message}`)
    },
  })
}

/**
 * Deploy a policy
 *
 * Mutation hook for deploying a DRAFT or INACTIVE policy to ACTIVE status.
 *
 * @param policyId - The policy draft ID to deploy
 * @returns Mutation object with mutate function and state
 */
export function useDeployPolicy(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return post<DeployResponse>(
        `/policy-drafts/${policyId}/deploy`
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      toast.success('Policy deployed successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to deploy policy: ${message}`)
    },
  })
}

/**
 * Undeploy an active policy
 *
 * Mutation hook for undeploying an ACTIVE policy to INACTIVE status.
 * Audit notes are optional.
 *
 * @param policyId - The policy draft ID to undeploy
 * @returns Mutation object with mutate function and state
 */
export function useUndeployPolicy(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: UndeployRequest = {}) => {
      return post<UndeployResponse, UndeployRequest>(
        `/policy-drafts/${policyId}/undeploy`,
        { notes: request.notes ?? '' }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      toast.success('Policy undeployed successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to undeploy policy: ${message}`)
    },
  })
}

/**
 * Validate a policy draft
 *
 * Mutation hook for running the validation pipeline on a policy.
 *
 * @param policyId - The policy draft ID to validate
 * @returns Mutation object with mutate function and state
 */
export function useValidatePolicy(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return post<ValidateResponse>(`/policy-drafts/${policyId}/validate`)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })
      queryClient.invalidateQueries({
        queryKey: policyKeys.lists(),
      })

      if (data.isValid) {
        toast.success('Policy validation passed')
      } else {
        toast.warning(`Policy validation found ${data.errors.length} errors`)
      }
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to validate policy: ${message}`)
    },
  })
}

/**
 * Generate an AI summary for a policy draft
 *
 * Calls the backend to analyze the XACML content and produce
 * a human-readable summary. Invalidates the policy detail cache.
 *
 * @param policyId - The policy draft ID to generate a summary for
 */
export function useGenerateSummary(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      return post<PolicyDraft>(`/policy-drafts/${policyId}/generate-summary`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })
      toast.success('AI summary generated successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to generate summary'
      toast.error(message)
    },
  })
}

/**
 * Update the human-readable summary of a policy draft
 *
 * @param policyId - The policy draft ID to update
 */
export function useUpdateSummary(policyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (summary: string) => {
      return put<PolicyDraft, UpdatePolicyRequest>(
        `/policy-drafts/${policyId}`,
        { humanReadable: summary }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: policyKeys.detail(policyId),
      })
      toast.success('Summary updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to update summary'
      toast.error(message)
    },
  })
}

/**
 * Export policies as a JSON bundle file download
 *
 * Supports exporting selected policies by ID or all policies.
 */
export function useExportPolicies() {
  return useMutation({
    mutationFn: async (params: { policyIds: string[] }) => {
      const response = await apiClient.post('/policy-drafts/export', params)
      const bundle = response.data.data

      const json = JSON.stringify(bundle, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `polix-policies-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    onSuccess: () => {
      toast.success('Policies exported successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'Failed to export policies'
      toast.error(message)
    },
  })
}

// ============================================================================
// CLIENT-SIDE UTILITIES
// ============================================================================

/**
 * Sort configuration for client-side sorting
 */
export interface SortConfig {
  column: 'name' | 'status' | 'createdAt' | 'updatedAt'
  direction: 'asc' | 'desc'
}

/**
 * Client-side sorting utility
 */
export function sortPolicies(
  policies: PolicyDraftListItem[],
  sortConfig: SortConfig
): PolicyDraftListItem[] {
  return [...policies].sort((a, b) => {
    let aValue: string | Date
    let bValue: string | Date

    switch (sortConfig.column) {
      case 'name':
        aValue = a.name.toLowerCase()
        bValue = b.name.toLowerCase()
        break
      case 'status':
        aValue = a.status
        bValue = b.status
        break
      case 'createdAt':
        aValue = new Date(a.createdAt)
        bValue = new Date(b.createdAt)
        break
      case 'updatedAt':
        aValue = new Date(a.updatedAt)
        bValue = new Date(b.updatedAt)
        break
      default:
        return 0
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
    return 0
  })
}

/**
 * Client-side search filtering utility
 */
export function filterPoliciesBySearch(
  policies: PolicyDraftListItem[],
  search: string
): PolicyDraftListItem[] {
  if (!search.trim()) return policies

  const searchLower = search.toLowerCase().trim()
  return policies.filter(
    (policy) =>
      policy.name.toLowerCase().includes(searchLower) ||
      policy.description?.toLowerCase().includes(searchLower)
  )
}
